import {
    Background,
    CFlex,
    CMargin,
    DText,
    Flex,
} from "../../../../styles/CommonStyles";
import Text from "../../../../core/utils/Text";
import Input from "../../../common/input/Input";
import ActionButton from "../../../common/buttons/ActionButton";
import CheckBox from "../../../common/input/CheckBox";
import { useEffect, useMemo, useState } from "react";
import { deepCopy } from "../../../../core/utils/common";
import { useQueryContext } from "../../../../core/contexts/query";
import { useAddCategory } from "../../../../core/services/react-query/setting/category";
import { useGetTickers, useGetAssistants } from "../../../../core/services/react-query/setting/predict";
import Select from "../../../common/dropdown/Select";
import { BiX } from "react-icons/bi";
import Tooltip from "../../../common/utils/Tooltip";

const initialCategory = {
    id: "",
    label: "",
    emoji: "",
    isActive: true,
    list: [], // Each item: { ticker, tickerCase, assistant }
};

const PairCategoryModal = ({
    onSuccess,
    type = "add", // 'add' | 'edit' | 'delete'
    _category,
    existingCategories = []
}) => {
    const { data: tickersData } = useGetTickers();
    const { data: assistantsData } = useGetAssistants();
    const { mutate: addCategory, isLoading: addLoading } = useAddCategory(onSuccess);
    const { setToast } = useQueryContext();

    const [category, setCategory] = useState(initialCategory);

    // --- Prefill data when editing ---
    useEffect(() => {
        if (_category) setCategory(_category);
    }, [_category]);

    const onInputChange = (v, key) => {
        setCategory((s) => ({ ...s, [key]: v }));
    };

    // --- Manage Ticker Rows ---
    const addTickerRow = () => {
        setCategory((s) => ({
            ...s,
            list: [{ ticker: "", tickerCase: "", assistant: "" }, ...s.list],
        }));
    };

    const removeTickerRow = (index) => {
        setCategory((s) => {
            const newList = deepCopy(s.list);
            newList.splice(index, 1);
            return { ...s, list: newList };
        });
    };

    const onTickerChange = (index, ticker) => {
        const tickerInfo = tickersData?.data?.find((t) => t.ticker === ticker);
        if (!tickerInfo) return;

        setCategory((s) => {
            const newList = deepCopy(s.list);
            newList[index].ticker = ticker;
            newList[index].tickerCase = tickerInfo.tickerCase;
            newList[index].assistant = "";
            return { ...s, list: newList };
        });
    };

    const onAssistantChange = (index, assistantValue) => {
        setCategory((s) => {
            const newList = deepCopy(s.list);
            newList[index].assistant = assistantValue;
            return { ...s, list: newList };
        });
    };

    // --- Validation ---
    const validAction = useMemo(() => {
        if (type === "delete") return true; // no validation needed for delete
        return (
            !!category.label &&
            category.list.length > 0 &&
            category.list.every((t) => t.ticker && t.tickerCase && t.assistant)
        );
    }, [category, type]);

    // --- Unified Action (Add / Edit / Delete) ---
    const onAction = () => {
        if (!validAction) {
            setToast({ message: "fill-inputs-error", isError: true, show: true });
            return;
        }

        let payload = deepCopy(existingCategories);

        if (type === "add") {
            // assign UUID to new category
            const newCategory = {
                ...category,
                id: crypto.randomUUID(),
                label: category.label,
                emoji: category.emoji || null,
                isActive: category.isActive,
                list: category.list.map((t) => ({
                    ticker: t.ticker,
                    tickerCase: t.tickerCase,
                    assistant: t.assistant,
                })),
            };

            payload = [...payload, newCategory];
        }

        if (type === "edit") {
            payload = payload.map((cat) =>
                cat.id === category.id ? { ...category } : cat
            );
        }

        if (type === "delete") {
            payload = payload.filter((cat) => cat.id !== category.id);
        }

        addCategory(payload);
        setToast({
            message:
                type === "delete"
                    ? "category-deleted"
                    : type === "edit"
                        ? "category-updated"
                        : "category-added",
            isError: false,
            show: true,
        });
    };

    return (
        <CFlex fw>
            <DText main fontSize={"b"}>
                <Text tid={`${type}-category`} />
            </DText>

            {type !== "delete" && (
                <>
                    <CMargin margin={"8px"} />
                    <Flex align={"center"} justify={"start"} style={{ marginTop: "8px", marginLeft: "auto" }}>
                        <CheckBox
                            active={category.isActive}
                            onClick={() =>
                                setCategory((s) => ({ ...s, isActive: !s.isActive }))
                            }
                        />
                        <DText main>
                            <Text tid={"is-active"} />
                        </DText>
                    </Flex>

                    <CMargin margin={"8px"} />
                    <Background bg={"mainBg"}>
                        <Input
                            value={category.label}
                            onInputChange={(v) => onInputChange(v, "label")}
                            label={"label"}
                        />
                    </Background>

                    <CMargin margin={"4px"} />

                    {/* Ticker Rows */}
                    <div
                        style={{
                            width: "100%",
                            minHeight: category.list.length > 0 ? "250px" : "0",
                            maxHeight: "250px",
                            overflowY: "auto",
                        }}
                    >
                        {category.list.map((t, index) => {
                            const tickerOptions =
                                tickersData?.data?.map((t) => t.ticker) || [];
                            const assistantsForTicker =
                                assistantsData?.data?.filter(
                                    (a) => a.tickerCase === t.tickerCase
                                ) || [];


                            return (
                                <>
                                    <Flex
                                        key={index}
                                        fw
                                        align="center"
                                        style={{ marginBottom: "8px" }}
                                    // className={`${index !== category.list.length - 1 ? `` : ""} py-1`}
                                    >
                                        <Select
                                            value={t.ticker}
                                            onValueChange={(idx) =>
                                                onTickerChange(index, tickerOptions[idx])
                                            }
                                            placeHolder="Select ticker"
                                            options={tickerOptions}
                                            width="50%"
                                            dropdownWidth="100%"
                                        />

                                        <Select
                                            value={t.assistant}
                                            onValueChange={(idx) =>
                                                onAssistantChange(
                                                    index,
                                                    assistantsForTicker[idx]?.value
                                                )
                                            }
                                            placeHolder="Select assistant"
                                            options={assistantsForTicker.map((a) => a.value)}
                                            width="50%"
                                            dropdownWidth="100%"
                                        />

                                        <Tooltip content={"remove-ticker"}>
                                            <button
                                                height="36px"
                                                onClick={() => removeTickerRow(index)}
                                                style={{
                                                    border: "none",
                                                    background: "transparent",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <BiX color="#c33" size={24} />
                                            </button>
                                        </Tooltip>
                                    </Flex>
                                </>
                            );
                        })}
                    </div>

                    <CMargin margin={"8px"} />
                    <ActionButton height="36px" onClick={addTickerRow} active>
                        Add Ticker
                    </ActionButton>
                </>
            )}

            <CMargin margin={"10px"} />
            <ActionButton
                active={validAction}
                height={"42px"}
                onClick={onAction}
                loading={addLoading}
            >
                <Text tid={type === "delete" ? "delete" : "submit"} />
            </ActionButton>
        </CFlex>
    );
};

export default PairCategoryModal;
